import React, { useState } from "react";
import PropTypes from 'prop-types';
import { IMaskInput } from 'react-imask';

const InputMaskDate = React.forwardRef(function InputMaskDate(props, ref) {
  const { inputRef, onChange, onFocus, onBlur, ...inputMaskProps } = props;
  const [focused, setFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);
 
  return (
    <IMaskInput
      {...inputMaskProps}
      inputRef={ref || inputRef}
      mask="00{/}00{/}0000"
      lazy={!focused && !hasValue}
      placeholderChar="_"
      overwrite
      prepare={(str, masked, flags) => {
        if (!flags.input) return str;
        const digits = masked.unmaskedValue;
 
        // Handle pasted dates in m/d/yyyy or mm/d/yyyy format
        if (str.length > 1 && str.includes('/') && digits.length === 0) {
          const parts = str.split('/');
          if (parts.length === 3) {
            let [month, day, year] = parts;
            if (month.length <= 2 && day.length <= 2 && year.length === 4) {
              const yearNum = parseInt(year, 10);
              const maxYear = new Date().getFullYear() + 1;
              const minYear = maxYear - 100;
              if (yearNum > minYear && yearNum <= maxYear) {
                if (month.length === 1) month = '0' + month;
                if (day.length === 1) day = '0' + day;
                return month + day + year;
              }
            }
          }
        }
 
        // Month first digit > 1 → pad with 0
        if (digits.length === 0 && parseInt(str) > 1) {
          return '0' + str;
        }
 
        // Month > 12 → pad month (01) and use the typed digit as day first digit (always pad to 0X)
        if (digits.length === 1 && digits !== '0' && parseInt(digits + str) > 12) {
          masked.reset();
          // Pad month and pad day: "0" + first_month_digit + "0" + typed_digit
          return '0' + digits + '0' + str;
        }
 
        // After month is complete (2 digits), next digit is day's first digit
        // If day first digit >= 3, pad it to 03, 04, 05... 09
        if (digits.length === 2 && parseInt(str) >= 3) {
          return '0' + str;
        }
 
        // Day second digit validation: day > 31 → reject
        if (digits.length === 3) {
          const dayFirstDigit = parseInt(digits[2]);
          const daySecondDigit = parseInt(str);
          const fullDay = dayFirstDigit * 10 + daySecondDigit;
          
          if (fullDay > 31) {
            // Day would be > 31, reject this digit
            return '';
          }
        }
 
        // "/" after single month digit → pad month (e.g., "1/" → "01/")
        if (str === '/' && digits.length === 1) {
          masked.reset();
          return '0' + digits;
        }
 
        // "/" after single day digit → pad day (e.g., "01/5/" → "01/05/")
        if (str === '/' && digits.length === 3) {
          const month = digits.substring(0, 2);
          masked.reset();
          return month + '0' + digits[2];
        }
 
        return str;
      }}
      onAccept={(value) => {
        const digits = value.replace(/[^0-9]/g, '');
        setHasValue(digits.length > 0);
        onChange({ target: { name: props.name, value } });
      }}
      onFocus={(e) => {
        setFocused(true);
        // Force cursor to the beginning of the field
        const inputEl = e.target;
        if (inputEl) {
          setTimeout(() => {
            // Sets both start and end selection to index 0
            inputEl.setSelectionRange(0, 0);
          }, 0);
        }
        if (onFocus) onFocus(e);
      }}
      onBlur={(e) => {
        setFocused(false);
        if (onBlur) onBlur(e);
      }}
    />
  );
});
 
InputMaskDate.displayName = 'InputMaskDate';

InputMaskDate.propTypes = {
  inputRef: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func
}
 
export default InputMaskDate;